import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { AREAS, CAMPO, CONFIGURACION, GENERAL, type EntradaMenu } from '@/app/navegacion'
import { useSesion } from '@/modules/auth/useSesion'
import { useSoyDeCampo } from '@/modules/campo/useSoyDeCampo'
import { NOMBRE_ROL } from '@/shared/dominio/roles'
import { MarcaMega } from '@/shared/marca/MarcaMega'
import {
  IconoAlistamiento, IconoArchivo, IconoAuditoria, IconoCartera, IconoChat, IconoClientes, IconoColaborador,
  IconoCotizacion, IconoFactura,
  IconoHerramienta, IconoInventario, IconoMapa, IconoMatriz, IconoMiTrabajo,
  IconoPunto, IconoSalir, IconoSobrantes, IconoSolicitudes, IconoTablero,
} from '@/shared/ui/Iconos/Iconos'
import { iniciales } from '@/shared/utilidades/formato'

import estilos from './MenuLateral.module.css'

/** Cada entrada dice qué icono la representa; aquí se resuelve a su dibujo. */
const ICONOS: Record<string, (p: { className?: string }) => React.ReactElement> = {
  tablero: IconoTablero,
  miTrabajo: IconoMiTrabajo,
  solicitudes: IconoSolicitudes,
  matriz: IconoMatriz,
  chat: IconoChat,
  herramienta: IconoHerramienta,
  mapa: IconoMapa,
  archivo: IconoArchivo,
  clientes: IconoClientes,
  inventario: IconoInventario,
  cotizacion: IconoCotizacion,
  factura: IconoFactura,
  colaborador: IconoColaborador,
  cartera: IconoCartera,
  alistamiento: IconoAlistamiento,
  auditoria: IconoAuditoria,
  sobrantes: IconoSobrantes,
}

function Icono({ clave }: { clave: string }) {
  const Dibujo = ICONOS[clave] ?? IconoPunto
  return <Dibujo />
}

type PropsMenuLateral = {
  abierto: boolean
  onCerrar: () => void
  /** Cuántas decisiones esperan en cada módulo, por ruta. */
  pendientes?: Record<string, number>
}

export function MenuLateral({ abierto, onCerrar, pendientes = {} }: PropsMenuLateral) {
  const { puede, perfil, salir } = useSesion()
  const deCampo = useSoyDeCampo()

  // Quien SOLO sale a campo. Un Gerente operativo vinculado a un colaborador
  // no entra aquí: necesita las dos cosas, su trabajo y la oficina.
  const soloCampo = perfil?.rol === 'operario'
  const { pathname } = useLocation()

  // El área que contiene la pantalla actual arranca desplegada; las demás
  // cerradas. Quien entra ve dónde está sin tener que buscarse.
  const areaActual = AREAS.find((a) =>
    a.entradas.some((e) => pathname === e.ruta || pathname.startsWith(e.ruta + '/')),
  )

  const [areasAbiertas, setAreasAbiertas] = useState<string[]>(() =>
    areaActual ? [areaActual.clave] : ['operacion'],
  )

  function alternarArea(clave: string) {
    setAreasAbiertas((previas) =>
      previas.includes(clave) ? previas.filter((c) => c !== clave) : [...previas, clave],
    )
  }

  function puedeVer(entrada: EntradaMenu) {
    return puede(entrada.modulo, entrada.accion ?? 'ver')
  }

  function dibujarEnlace(entrada: EntradaMenu) {
    return (
      <NavLink
        key={entrada.ruta}
        to={entrada.ruta}
        end={entrada.ruta === '/'}
        onClick={onCerrar}
        className={({ isActive }) => `${estilos.enlace} ${isActive ? estilos.activo : ''}`}
      >
        <Icono clave={entrada.icono} />
        {entrada.etiqueta}
      </NavLink>
    )
  }

  // «Mi trabajo» solo para quien de verdad sale a la calle.
  const generalVisible = GENERAL.filter((e) => puedeVer(e) && (!e.soloCampo || deCampo))
  const configuracionVisible = CONFIGURACION.filter(puedeVer)

  return (
    <>
      {abierto && <div className={estilos.velo} onClick={onCerrar} aria-hidden="true" />}

      <aside className={`${estilos.menu} ${abierto ? estilos.abierto : ''} no-imprimir`}>
        <div className={estilos.cabecera}>
          <MarcaMega tamanoLogo={26} />
        </div>

        <nav className={`${estilos.navegacion} desplazable`} aria-label="Menú principal">
          {/* El técnico no ve las áreas de la empresa ni las secciones del
              PDF: son pantallas de oficina. Ve sus tres cosas y nada más. */}
          {soloCampo ? (
            CAMPO.filter(puedeVer).map(dibujarEnlace)
          ) : (
            <>
          {generalVisible.length > 0 && (
            <>
              <p className={estilos.grupo}>
                General<span className={estilos.linea} aria-hidden="true" />
              </p>
              {generalVisible.map(dibujarEnlace)}
            </>
          )}

          <p className={estilos.grupo}>
            Áreas de la empresa<span className={estilos.linea} aria-hidden="true" />
          </p>

          {AREAS.map((area) => {
            const visibles = area.entradas.filter(puedeVer)
            if (visibles.length === 0) return null

            const abierta = areasAbiertas.includes(area.clave)

            return (
              <div key={area.clave} className={abierta ? estilos.areaAbierta : undefined}>
                <button
                  type="button"
                  className={estilos.areaCabecera}
                  onClick={() => alternarArea(area.clave)}
                  aria-expanded={abierta}
                >
                  <span className={estilos.flecha} aria-hidden="true">
                    ▶
                  </span>
                  <span className={estilos.areaNombre}>{area.nombre}</span>
                </button>

                <div className={estilos.modulos}>
                  {visibles.map((entrada) => {
                    const cuantos = pendientes[entrada.ruta] ?? 0

                    return (
                      <NavLink
                        key={entrada.ruta}
                        to={entrada.ruta}
                        onClick={onCerrar}
                        className={({ isActive }) =>
                          [
                            estilos.modulo,
                            isActive ? estilos.moduloActivo : '',
                            entrada.lista ? '' : estilos.pendiente,
                          ]
                            .filter(Boolean)
                            .join(' ')
                        }
                      >
                        <Icono clave={entrada.icono} />
                        {entrada.etiqueta}
                        {cuantos > 0 && <span className={estilos.contador}>{cuantos}</span>}
                      </NavLink>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {configuracionVisible.length > 0 && (
            <>
              <p className={estilos.grupo}>
                Configuración<span className={estilos.linea} aria-hidden="true" />
              </p>
              {configuracionVisible.map(dibujarEnlace)}
            </>
          )}
            </>
          )}
        </nav>

        {/* EL PIE, QUE NO EXISTÍA.
            El menú se quedaba sin enlaces a media altura y de ahí al borde
            inferior era morado plano: casi la mitad del riel vacía. No era
            sobriedad, era un hueco —y encima la identidad de quien está dentro
            vivía arriba a la derecha, lejos de donde se la busca—.
            Anclar aquí quién es y cómo salir cierra el riel por abajo y hace
            que el vacío de en medio se lea como aire y no como falta. */}
        {perfil && (
          <div className={estilos.pie}>
            <span className={estilos.avatar} aria-hidden="true">
              {iniciales(perfil.nombre)}
            </span>

            <span className={estilos.quien}>
              <span className={estilos.nombre}>{perfil.nombre}</span>
              <span className={estilos.cargo}>{NOMBRE_ROL[perfil.rol]}</span>
            </span>

            <button
              type="button"
              className={estilos.salir}
              onClick={() => void salir()}
              title="Salir"
              aria-label="Salir"
            >
              <IconoSalir />
            </button>
          </div>
        )}
      </aside>
    </>
  )
}
